import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Container, Box, Typography, AppBar, Toolbar, Tooltip, Avatar, IconButton, Menu, MenuItem, Badge, styled, alpha, InputBase, Popper, Fade, Paper, ClickAwayListener, CircularProgress, Divider, Button, List, ListItem, ListItemAvatar, ListItemText, ListItemButton, Stack, Drawer, ToggleButton, ToggleButtonGroup, PaletteMode, ListItemIcon } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store';
import { signOut } from '../store/thunks/authThunks';
import { ChatBubbleRounded, DarkModeRounded, LightModeRounded, LogoutRounded, MarkAsUnreadRounded, MarkEmailReadRounded, NotificationsRounded, SearchRounded, SettingsRounded } from '@mui/icons-material';
import { useDebouncedCallback } from "use-debounce"; 
import { collection, DocumentData, getCountFromServer, getDoc, getDocs, getFirestore, limit, orderBy, query, runTransaction, Timestamp, Unsubscribe, where, writeBatch } from 'firebase/firestore';
import LinkBehavior from './LinkBehavior';
import { color } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { dark, light } from '../store/slices/appSlice';
import { queryNotifications } from '../utils/queryNotifications';
import { Notification } from '../types/Notification';
import { DateTime } from 'luxon';
import notificationConverter from '../converters/notificationConverter';
import { queryPassportDocumentsCountExpiresIn } from '../utils/queryPersonalDocuments';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 20,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  display: 'flex',
  flexDirection: 'row',
  flex: 4,
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  display: 'flex',
  flex: 1,
  marginRight: theme.spacing(1),
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    display: 'flex',
    flex: 1,
  },
}));

export const EMSAppBar = () => {
  const db = getFirestore();

  const navigate = useNavigate();
  const dispath = useAppDispatch();
  const authState = useAppSelector((s) => s.auth);
  const mode = useAppSelector((s) => s.app.mode);

  const adminOnly = authState.URB && authState.URB.roles.some((r: string) => ['super', 'admin'].includes(r));

  // Search variables
  const searchDivRef = useRef<HTMLDivElement>(null);
  const [anchorElSearch, setAnchorElSearch] = React.useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<Array<DocumentData>>([]);

  const [openSettings, setOpenSettings] = useState(false);

  const [anchorElNotifications, setAnchorElNotifications] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [hasUnreadInit, setHasUnreadInit] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  const [loadingMarkAllRead, setLoadingMarkAllRead] = useState(false);

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const [passportExpireCount, setPassportExpireCount] = useState(0);

  // Search functions
  const searchDebounced = useDebouncedCallback((value) => {
    setSearch(value);
    setSearching(true);
    setAnchorElSearch(searchDivRef.current);
    setSearchResult([]);
    getDocs(
      query(collection(db, 'Employees'), 
      where('idNumber', '>=', value), 
      where('idNumber', '<=', value + '\uf8ff'),
      limit(10),
      orderBy('idNumber'))
    ).then((qSnapshot) => {
        setSearchResult(qSnapshot.docs.map((d) => d.data()));
        setSearching(false);
      });
  }, 1000);

  const searchOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    searchDebounced(event.currentTarget.value);
  };

  const searchOnFocus = () => {
    if (search.trim() !== '') {
      setAnchorElSearch(searchDivRef.current);
    }
  };

  const searchResultItemOnLick = (idNumber: string) => {
    setAnchorElSearch(null);
    navigate(`/home/employees/${idNumber}`)
  };

  const searchResultItemChatOnClick = (idNumber: string) => {
    setAnchorElSearch(null);
    navigate(`/messages/`)
  };
  // Search functions - end

  const handleOpenSettingsOnClick = () => {
    setOpenSettings(true);
  };

  const handleCloseSettingsOnClick = () => {
    setOpenSettings(false);
  };

  const handleModeOnChange = (event: React.MouseEvent<HTMLElement>, n: PaletteMode) => {
    if (n === 'light') dispath(light());
    else if (n === 'dark') dispath(dark());
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleSignoutOnPress = () => {
    setAnchorElUser(null);
    dispath(signOut());
  };

  const handlePassportExpireOnClick = () => {
    setAnchorElNotifications(null);
    navigate(`/home/notifications/personalDocuments`);
  };

  const handleViewAllNotifs = () => {
    setAnchorElNotifications(null);
    navigate(`/home/notifications`);
  };

  const handleMarkAllAsRead = async () => {
    if (authState.isSignedIn && hasUnread) {
      setLoadingMarkAllRead(true);

      let count = 1;
      let batch = writeBatch(db);
      const qSnapshot = await getDocs(query(collection(db, `Employees/${authState.idNumber}/Notifications`), where('status', '==', 'Unread')).withConverter(notificationConverter));
      try {
        qSnapshot.docs.forEach(qDocSnapshot => {
          const ref = qDocSnapshot.ref;
          batch.set(ref, { 
            status: 'Read', 
            dateRead: DateTime.utc().toISO(),
            lastUpdate: Timestamp.now() 
          }, {merge: true});
                
          count++;
          if (count % 500 === 0) {
            batch.commit();
            batch = writeBatch(db);
          }
        });
        await batch.commit();
        setHasUnread(false);
      } catch {
      }

      setLoadingMarkAllRead(false);
    }
  };

  const handleNotificationItemOnClick = (n: Notification) => {
    setAnchorElNotifications(null);
    navigate(`/home/notifications/${n.id}`, {
      state: n,
    });
  };

  useEffect(() => {
    let notificationsUnsubscribe: Unsubscribe | undefined = undefined
    if (authState.isSignedIn) {
      notificationsUnsubscribe = queryNotifications(authState.idNumber, (notifications: Notification[]) => {
        setNotifications(notifications);
        if (hasUnreadInit) {
          setHasUnread(notifications.some((n) => n.status === 'Unread'));
        } else {
          // Get unread count
          const q = query(collection(db, `Employees/${authState.idNumber}/Notifications`), where('status', '==', 'Unread')).withConverter(notificationConverter);
          getCountFromServer(q).then(r => {
            setHasUnread(r.data().count > 0);
            setHasUnreadInit(true);
          })
        }
      });

      queryPassportDocumentsCountExpiresIn().then(c => {
        setPassportExpireCount(c);
      });
    }

    return () => {
      notificationsUnsubscribe && notificationsUnsubscribe();
    }
  }, [])

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <ClickAwayListener onClickAway={() => setAnchorElSearch(null)}>
              <div style={{ display: 'flex', flexGrow: 1 }}>
                <Search ref={searchDivRef}>
                  <SearchIconWrapper>
                    <SearchRounded />
                  </SearchIconWrapper>
                  <StyledInputBase
                    placeholder="Search…"
                    inputProps={{ 'aria-label': 'search' }}
                    onChange={searchOnChanged}
                    onFocus={searchOnFocus}
                  />
                </Search>
                <Popper open={Boolean(anchorElSearch)} anchorEl={anchorElSearch} placement="bottom-start" transition sx={{ zIndex: 9999 }}>
                  {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                      <Paper sx={{ mt: 0.5, width: 600, filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))' }} elevation={12}>
                        <Box sx={{ alignItems: 'center', justifyContent: 'center' }}>
                          {searching && (
                            <CircularProgress />
                          )}
                          <List>
                            {searchResult.map((d) => {
                              return (
                                <ListItem
                                  key={d.uid} 
                                  secondaryAction={
                                    <IconButton onClick={() => searchResultItemChatOnClick(d.idNumber)} edge="end" aria-label="comments">
                                      <ChatBubbleRounded />
                                    </IconButton>
                                  }
                                  disablePadding
                                >
                                  <ListItemButton dense onClick={() => searchResultItemOnLick(d.idNumber)}>
                                    <ListItemAvatar>
                                      <Avatar alt={d.name} src={d.picture} />
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={
                                        <Typography sx={{ fontWeight: 700, lineHeight: 'normal', mt: 0.4 }}>
                                          {d.name}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 'normal' }}>
                                          {d.idNumber}
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                          <Divider />
                          <Box p={1} display="flex" flexDirection="row" justifyContent="end">
                            <Button disabled={searching || searchResult.length === 0}>View All</Button>
                          </Box>
                        </Box>
                      </Paper>
                    </Fade>
                  )}
                </Popper>
              </div>
            </ClickAwayListener>
            <Box flex={1} />
            <Stack direction="row" spacing={2} sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenSettingsOnClick}>
                  <Badge badgeContent={0} color="error">
                    <SettingsRounded sx={{ color: '#fff' }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              {adminOnly && (
                <Tooltip title="Messages">
                  <IconButton onClick={() => navigate('/messages', { replace: true })}>
                    <Badge badgeContent={0} color="error">
                      <ChatBubbleRounded sx={{ color: '#fff' }} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
              <ClickAwayListener onClickAway={() => setAnchorElNotifications(null)}>
                <div>
                  <Tooltip title="View notifications">
                    <IconButton onClick={(e) => setAnchorElNotifications(Boolean(anchorElNotifications) ? null : e.currentTarget)}>
                      <Badge variant='dot' color="error" invisible={!hasUnread && passportExpireCount === 0}>
                        <NotificationsRounded sx={{ color: '#fff' }} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Popper open={Boolean(anchorElNotifications)} anchorEl={anchorElNotifications} placement="bottom-end" sx={{ zIndex: 9999 }}>
                    <Paper sx={{ mt: 0.5, width: 650 }} elevation={12}>
                      <Stack direction='row' spacing={1} p={1} pb={0}>
                        <Badge variant='standard' color='error' badgeContent={passportExpireCount}>
                          <Button color={passportExpireCount > 0 ? 'error' : 'info'} variant='outlined' onClick={handlePassportExpireOnClick}>Passport</Button>
                        </Badge>
                        <Box flex={1} />
                        <Button startIcon={loadingMarkAllRead ? <CircularProgress size={24} /> : <MarkAsUnreadRounded />} disabled={!hasUnread || loadingMarkAllRead} onClick={handleMarkAllAsRead}>Mark All as Read</Button>
                        <Button startIcon={<NotificationsRounded />} onClick={handleViewAllNotifs}>View All</Button>
                      </Stack>
                      <List>
                        {notifications.map(n => {
                          return (
                            <ListItem key={n.id} disablePadding disableGutters onClick={() => handleNotificationItemOnClick(n)}>
                              <ListItemButton>
                                <ListItemText 
                                  primary={
                                    <Badge variant='dot' color="primary" invisible={n.status === 'Read'}>
                                      <Box mr={2}>
                                        <Typography fontWeight={n.status === 'Unread' ? '700' : 'normal'}>{n.title}</Typography>
                                      </Box>
                                    </Badge>
                                  }
                                  secondary={
                                    <React.Fragment>
                                      <Typography
                                        sx={{ display: 'inline' }}
                                        component="span"
                                        variant="body2"
                                        color="text.primary"
                                      >
                                        {DateTime.fromISO(n.dateCreated).toLocaleString(DateTime.DATETIME_SHORT)}
                                      </Typography>
                                      {` — ${n.body}`}
                                    </React.Fragment>
                                  } />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  </Popper>
                </div>
              </ClickAwayListener>
              <Tooltip title="Open menu">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar alt={authState.name} src={authState.picture} />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem onClick={handleSignoutOnPress} sx={{ width: 200 }}>
                  <ListItemIcon>
                    <LogoutRounded />
                  </ListItemIcon>
                  <ListItemText>Sign out</ListItemText>
                </MenuItem>
              </Menu>
            </Stack>
          </Toolbar>
        </Container>
        <Drawer anchor="right" open={openSettings} onClose={handleCloseSettingsOnClick} sx={{ borderTopLeftRadius: 16, borderBottomLeftRadius: 16, backdropFilter: 'blur(3px)' }}>
          <Box sx={{ width: 400 }}>
            <Box py={3} px={2}>
              <Typography variant="h5">Settings</Typography>
            </Box>
            <Divider />
            <Box px={2}>
              <Typography variant="caption" component="h6" sx={{ m: '20px 0 10px' }}>MODE</Typography>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeOnChange}
                aria-label="App theme mode"
                fullWidth
              >
                <ToggleButton value="light" aria-label="light mode">
                  <LightModeRounded sx={{ mr: 1 }} />LIGHT
                </ToggleButton>
                <ToggleButton value="dark" aria-label="dark mode">
                  <DarkModeRounded sx={{ mr: 1 }} />DARK
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Drawer>
      </AppBar>
    </>
  );
};
